import { z } from 'zod';
export const AUTO_TRANSLATE_KEY = 'llm.auto_translate_inbound';
/** WRITE boundary: shape stored under {@link AUTO_TRANSLATE_KEY} in app_settings.
 *  `.strict()` on the WRITE path so a typo'd/extra field at write time is rejected
 *  rather than silently stored. Use `AutoTranslateStoredSetting` (below) on the
 *  READ path instead — it tolerates additive unknown fields from a newer writer
 *  while still enforcing `enabled: boolean` (corrupt enabled → fail-safe OFF).
 *
 *  Rolling-deploy safety: a NEWER writer that adds `{enabled, foo}` to the stored
 *  jsonb will be parsed successfully by an OLDER reader using
 *  `AutoTranslateStoredSetting.passthrough()` — no accidental flip-to-OFF during
 *  a rolling deploy. The `enabled` type check remains strict on both schemas: a
 *  non-boolean `enabled` (e.g. `{enabled:'true'}`) still fails parse → fail-safe OFF.
 *  The `.strict()` on `UpdateLlmSettingsRequest` below is a separate concern
 *  (reject typo'd API payloads) and is unaffected by this note. */
export const AutoTranslateSetting = z.object({ enabled: z.boolean() }).strict();
/** READ boundary: tolerates additive unknown fields from a newer writer while
 *  still enforcing `enabled: boolean` (unknown additive fields are passed through,
 *  a non-boolean or missing `enabled` still fails → fail-safe OFF).
 *  Use this schema in all READERS (worker auto-translator + admin-api GET). */
export const AutoTranslateStoredSetting = z.object({ enabled: z.boolean() }).passthrough();
export const SUPPRESS_NON_CUSTOMER_KEY = 'llm.suppress_non_customer_pipelines';
/** WRITE boundary for {@link SUPPRESS_NON_CUSTOMER_KEY} — mirrors AutoTranslateSetting. */
export const SuppressNonCustomerSetting = z.object({ enabled: z.boolean() }).strict();
/** READ boundary for {@link SUPPRESS_NON_CUSTOMER_KEY}: additive unknown fields from a
 *  newer writer pass through; a non-boolean/missing `enabled` fails → fail-safe OFF
 *  (no suppression = today's behaviour). Mirrors AutoTranslateStoredSetting. */
export const SuppressNonCustomerStoredSetting = z.object({ enabled: z.boolean() }).passthrough();
export const RERUN_ON_REPLY_KEY = 'pipeline.rerun_on_reply';
/** WRITE boundary for {@link RERUN_ON_REPLY_KEY} — mirrors AutoTranslateSetting.
 *
 *  D2. Master switch for re-running the classify → gate → pipeline cycle on EVERY
 *  new inbound message in an already-classified conversation (today the cycle runs
 *  once per conversation). OFF = today's behaviour bit-for-bit. */
export const RerunOnReplySetting = z.object({ enabled: z.boolean() }).strict();
/** READ boundary for {@link RERUN_ON_REPLY_KEY}: additive unknown fields from a newer
 *  writer pass through; a non-boolean/missing `enabled` fails → fail-safe OFF (no
 *  re-runs = today's behaviour). Mirrors AutoTranslateStoredSetting. */
export const RerunOnReplyStoredSetting = z.object({ enabled: z.boolean() }).passthrough();
export const RESOLVE_ALT_EMAIL_KEY = 'identity.resolve_alt_email';
/** WRITE boundary for {@link RESOLVE_ALT_EMAIL_KEY} — mirrors AutoTranslateSetting.
 *
 *  D3-A. When ON, a miss on the SENDER's address makes the identity resolver
 *  extract the account address the customer typed in the BODY of the letter and
 *  retry the lookup with it (measured: closes 23 of 129 unidentified dialogs).
 *  OFF = today's behaviour bit-for-bit: one lookup, by sender address only. */
export const ResolveAltEmailSetting = z.object({ enabled: z.boolean() }).strict();
/** READ boundary for {@link RESOLVE_ALT_EMAIL_KEY}: additive unknown fields from a
 *  newer writer pass through; a non-boolean/missing `enabled` fails → fail-safe OFF
 *  (no second lookup, no LLM call). Mirrors AutoTranslateStoredSetting. */
export const ResolveAltEmailStoredSetting = z.object({ enabled: z.boolean() }).passthrough();
export const INBOX_SORT_BY_ACTIVITY_KEY = 'inbox.sort_by_activity';
/** WRITE boundary for {@link INBOX_SORT_BY_ACTIVITY_KEY} — mirrors AutoTranslateSetting.
 *
 *  NOT an LLM flag despite living in this file: the batch keeps ONE settings
 *  contract + ONE settings screen for every `app_settings` toggle it introduces,
 *  rather than growing a route and a contract per flag. `LlmSettings` below is the
 *  batch's whole settings payload; the file name is historical. */
export const InboxSortByActivitySetting = z.object({ enabled: z.boolean() }).strict();
/** READ boundary for {@link INBOX_SORT_BY_ACTIVITY_KEY}: additive unknown fields from a
 *  newer writer pass through; a non-boolean/missing `enabled` fails → fail-safe OFF
 *  (inbox keeps sorting by `conversation.updated_at`, i.e. today's behaviour).
 *  Mirrors AutoTranslateStoredSetting. */
export const InboxSortByActivityStoredSetting = z.object({ enabled: z.boolean() }).passthrough();
// WRITE boundary. ALL fields are OPTIONAL — this is a PARTIAL patch, and that is
// load-bearing for rolling deploys: a browser-cached OLD SPA PATCHes only
// `{autoTranslateInbound}`, and a required second field would 400 it. `.strict()`
// still rejects typo'd/unknown fields. An empty `{}` is a legal no-op.
export const UpdateLlmSettingsRequest = z
    .object({
    autoTranslateInbound: z.boolean().optional(),
    suppressNonCustomerPipelines: z.boolean().optional(),
    sortInboxByActivity: z.boolean().optional(),
    rerunPipelineOnReply: z.boolean().optional(),
    resolveAltEmail: z.boolean().optional(),
})
    .strict();
// READ boundary (GET response). Declared independently rather than derived from
// UpdateLlmSettingsRequest: the request is partial, but the response ALWAYS
// carries the resolved value of every flag.
export const LlmSettings = z.object({
    autoTranslateInbound: z.boolean(),
    suppressNonCustomerPipelines: z.boolean(),
    sortInboxByActivity: z.boolean(),
    rerunPipelineOnReply: z.boolean(),
    resolveAltEmail: z.boolean(),
});
//# sourceMappingURL=llm-settings.js.map